/**
 * Storage wrapper that uses Capacitor Preferences on native (survives WebView
 * data clears and is the OS-recommended path for app-local state) and falls
 * back to localStorage on the browser. Both code paths are sync-shaped from
 * the caller's POV — we wrap Preferences (which is Promise-based) with an
 * eagerly-hydrated cache so existing localStorage call sites don't have to
 * become async.
 *
 * On native first-launch we also run a one-time migration: anything the
 * legacy TWA wrote to localStorage at https://app.lotngo.com is read and
 * mirrored into Preferences. Because Capacitor is configured with
 * server.hostname = 'app.lotngo.com', the WebView's localStorage points at
 * the same origin the TWA used — so the legacy reads succeed without any
 * server hand-off.
 *
 * Keys covered: anything Zustand persisted under 'lotngo-auth' (refresh
 * token, user, push-notifications-enabled flag). The accessToken is NOT
 * persisted by design (see authStore.ts:155) — silent refresh re-issues it.
 */
import { isNativePlatform } from './runtime';

const LEGACY_LOCALSTORAGE_KEYS = ['lotngo-auth', 'lotngo-language'] as const;
const MIGRATION_MARKER_KEY     = '__migrated_from_twa_v1';

interface PreferencesShape {
  get:    (opts: { key: string }) => Promise<{ value: string | null }>;
  set:    (opts: { key: string; value: string }) => Promise<void>;
  remove: (opts: { key: string }) => Promise<void>;
}

let preferencesCache: PreferencesShape | null = null;
function getPreferences(): PreferencesShape | null {
  if (preferencesCache) return preferencesCache;
  if (!isNativePlatform()) return null;
  // The npm wrapper @capacitor/preferences isn't installed in packages/web,
  // so `import('@capacitor/preferences')` is a bare specifier the WebView
  // can't resolve at runtime — it throws and the catch above used to silently
  // swallow it, leaving memoryCache empty and the user logged out on every
  // cold launch. Reach into the global bridge instead; the native shell
  // registers the plugin under window.Capacitor.Plugins.Preferences.
  const plugin = (globalThis as any).Capacitor?.Plugins?.Preferences as PreferencesShape | undefined;
  if (!plugin) return null;
  preferencesCache = plugin;
  return preferencesCache;
}

/**
 * One-shot migration. Idempotent — sets a marker after running so subsequent
 * launches skip the work. Call from the app bootstrap (main.tsx) BEFORE the
 * Zustand store hydrates from storage.
 */
export async function migrateLegacyStorageIfNeeded(): Promise<void> {
  if (!isNativePlatform()) return;
  const prefs = getPreferences();
  if (!prefs) return;

  const marker = await prefs.get({ key: MIGRATION_MARKER_KEY });
  if (marker.value) return;

  for (const key of LEGACY_LOCALSTORAGE_KEYS) {
    const legacy = localStorage.getItem(key);
    if (legacy !== null) {
      await prefs.set({ key, value: legacy });
    }
  }
  await prefs.set({ key: MIGRATION_MARKER_KEY, value: new Date().toISOString() });
}

/**
 * Custom Zustand persist storage. Reads/writes through Preferences on native;
 * passes through to localStorage on the browser. Same shape Zustand expects
 * from its `createJSONStorage`.
 *
 * The native path caches values in memory and writes through asynchronously
 * so the Zustand sync interface stays satisfied. On hydration the store
 * waits one tick for the async read to land — see the `onRehydrateStorage`
 * hook below.
 */
const memoryCache = new Map<string, string>();

export const persistedStorage = {
  getItem(key: string): string | null {
    if (!isNativePlatform()) return localStorage.getItem(key);
    return memoryCache.get(key) ?? null;
  },
  setItem(key: string, value: string): void {
    if (!isNativePlatform()) {
      localStorage.setItem(key, value);
      return;
    }
    memoryCache.set(key, value);
    const p = getPreferences();
    if (p) void p.set({ key, value });
  },
  removeItem(key: string): void {
    if (!isNativePlatform()) {
      localStorage.removeItem(key);
      return;
    }
    memoryCache.delete(key);
    const p = getPreferences();
    if (p) void p.remove({ key });
  },
};

/**
 * Pre-fill memoryCache from Preferences. Must be awaited BEFORE the Zustand
 * store reads `getItem` during hydration — otherwise it sees empty memory.
 * Called once from the app bootstrap right after migrateLegacyStorageIfNeeded.
 */
export async function hydratePersistedStorage(): Promise<void> {
  if (!isNativePlatform()) return;
  const prefs = getPreferences();
  if (!prefs) return;
  for (const key of LEGACY_LOCALSTORAGE_KEYS) {
    const v = await prefs.get({ key });
    if (v.value !== null) memoryCache.set(key, v.value);
  }
}
